import { useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import Title from '@/components/ui/Title';
import { PaymentIcon } from 'react-svg-credit-card-payment-icons';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  detachPaymentMethod,
  fetchPaymentMethods,
  setPaymentMethodDefault,
} from '@/features/billing/billingThunks';
import {
  selectPaymentMethods,
  selectSetDefaultLoading,
  selectDetachPaymentMethodLoading,
} from '@/features/billing/billingSlice';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const getCardIconType = (brand: string): string => {
  const b = brand.toLowerCase().replace(/\s+/g, '');
  const map: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'AmericanExpress',
    americanexpress: 'AmericanExpress',
    american_express: 'AmericanExpress',
    discover: 'Discover',
    diners: 'DinersClub',
    diners_club: 'DinersClub',
    dinersclub: 'DinersClub',
    jcb: 'Jcb',
    unionpay: 'Unionpay',
    maestro: 'Maestro',
    elo: 'Elo',
    hiper: 'Hiper',
    hipercard: 'Hipercard',
    mir: 'Mir',
  };
  return map[b] ?? 'Generic';
};

export default function CardList() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('settings');
  const workspaceId = useAppSelector((s: RootState) => s.workspace.currentWorkspace?.id);
  const paymentMethods = useAppSelector(selectPaymentMethods);
  const setDefaultLoading = useAppSelector(selectSetDefaultLoading);
  const detachLoading = useAppSelector(selectDetachPaymentMethodLoading);

  const handleSetDefault = async (paymentMethodId: string) => {
    if (!workspaceId) return;
    try {
      await dispatch(setPaymentMethodDefault({ workspaceId, paymentMethodId })).unwrap();
      await dispatch(fetchPaymentMethods(workspaceId));
      toast.success(t('cardList.toast.defaultUpdated'));
    } catch {
      toast.error(t('cardList.toast.defaultError'));
    }
  };

  const handleDetachCard = async (paymentMethodId: string) => {
    if (!workspaceId) return;
    try {
      await dispatch(detachPaymentMethod({ workspaceId, paymentMethodId })).unwrap();
      await dispatch(fetchPaymentMethods(workspaceId));
      toast.success(t('cardList.toast.deleted'));
    } catch {
      toast.error(t('cardList.toast.deleteError'));
    }
  };

  useEffect(() => {
    if (workspaceId != null) {
      dispatch(fetchPaymentMethods(workspaceId));
    }
  }, [workspaceId, dispatch]);

  const cardsToDisplay = paymentMethods ?? [];

  if (cardsToDisplay.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('cardList.noCards')}</p>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {cardsToDisplay.map((card) => (
        <Card key={card.id}>
          <div className="flex items-start gap-4">
            <div className="shrink-0">
              <PaymentIcon
                type={
                  getCardIconType(card.brand) as React.ComponentProps<typeof PaymentIcon>['type']
                }
                format="flatRounded"
                width={46}
                style={{ display: 'block' }}
                aria-label={`${card.brand} logo`}
              />
            </div>
            <div className="w-full">
              <div className="flex items-center w-full justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Title level={2}>
                    {card.brand.charAt(0).toUpperCase() + card.brand.slice(1)} ----{card.last4}
                  </Title>
                </div>
                <div className="flex items-center gap-4">
                  <p>{t('cardList.usedByDefault')}</p>
                  <Switch
                    checked={card.isDefault}
                    onCheckedChange={(checked) => checked && handleSetDefault(card.id)}
                    disabled={setDefaultLoading || cardsToDisplay.length === 1}
                  />
                </div>
              </div>
              <p className="text-sm text-gray-500">
                {t('cardList.expiresOn')} {card.expMonth.toString().padStart(2, '0')}/{card.expYear}
              </p>

              <button
                className="text-sm font-medium pt-4 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => handleDetachCard(card.id)}
                disabled={detachLoading || cardsToDisplay.length === 1}
              >
                {t('cardList.deleteCard')}
              </button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
